import { useState, useEffect } from 'react';
import { Event } from 'nostr-tools';
import { getNostrPool } from '../lib/nostrPool';
import { RELAYS } from '../config/constants';
import { useAdmin } from '../contexts/AdminContext';
import { useGlobalModeration } from './useGlobalModeration';

/**
 * Fetch events filtered by hashtag
 * @param hashtag - The hashtag to filter by (without #)
 * @param limit - Maximum number of events to fetch
 */
export function useHashtagFeed(hashtag: string | null, limit = 50) {
    const [events, setEvents] = useState<Event[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const pool = getNostrPool();
    const { settings } = useAdmin();
    const { deletedEventIds, bannedWords } = settings;
    const { blockedEventIds } = useGlobalModeration();

    useEffect(() => {
        if (!hashtag) {
            setIsLoading(false);
            return;
        }

        setIsLoading(true);
        setEvents([]);
        const seenEvents = new Set<string>();

        const filter = {
            kinds: [1],
            '#t': [hashtag.toLowerCase()],
            limit,
        };

        const sub = pool.subscribeMany(RELAYS, filter, {
            onevent(event: Event) {
                if (seenEvents.has(event.id)) return;

                // Filter deleted and blocked events
                if (deletedEventIds.includes(event.id)) return;
                if (blockedEventIds.has(event.id)) return;

                // Filter banned words
                if (bannedWords.some(word => event.content.toLowerCase().includes(word.toLowerCase()))) return;

                seenEvents.add(event.id);

                setEvents((prev) => {
                    const exists = prev.some(e => e.id === event.id);
                    if (exists) return prev;

                    const newEvents = [...prev, event];
                    // Sort by creation time (newest first)
                    return newEvents.sort((a, b) => b.created_at - a.created_at);
                });
            },
            oneose() {
                setIsLoading(false);
            },
        });

        return () => {
            sub.close();
        };
    }, [hashtag, limit, deletedEventIds, bannedWords, blockedEventIds]);

    return { events, isLoading };
}
